import { useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { PERMISSIONS } from "constants/permissions"

import { AdminTokenService } from "services/token/AdminTokenService"

import { formatDecimalString, formatUsdCents, isDecimalString, tokenErrorCopy, Translate, useIdempotencyKey } from "../helpers/token.helpers"
import { useTokenIdentity } from "../helpers/useTokenIdentity"

import { ReasonModal } from "./shared/ReasonModal"
import { TokenField } from "./shared/TokenField"

/**
 * The three manual money operations.
 *
 * 🔴 SPEC §8.4: "these three operations are how every casino gets hit with
 * insider abuse if access is too loose."
 *
 * Business B3 declined admin 2FA for V1. The protection is therefore exactly
 * three things — the `TOKEN_MANUAL_OPS` permission, a MANDATORY reason, and the
 * audit trail — and that is the complete list. Nothing in this file may make any
 * of them optional, pre-filled, or bypassable, and the copy below says so out
 * loud because an operator who understands why the field is there writes a
 * better reason than one who thinks it is a formality.
 *
 * Every operation carries an idempotency key generated once per dialog opening.
 * That is what stops a double-click booking twice; see `useIdempotencyKey`.
 */

type OpKind = "adjust" | "reconcile" | "terminate"

/** Translation keys per operation; the ticker is interpolated at render. */
const OPS: Array<{ kind: OpKind; titleKey: string; blurbKey: string; warningKey: string }> = [
  { kind: "adjust", titleKey: "token.adjustBalance", blurbKey: "token.adjustBlurb", warningKey: "token.adjustWarning" },
  { kind: "reconcile", titleKey: "token.reconcilePurchase", blurbKey: "token.reconcileBlurb", warningKey: "token.reconcileWarning" },
  { kind: "terminate", titleKey: "token.terminatePosition", blurbKey: "token.terminateBlurb", warningKey: "token.terminateWarning" }
]

export const ManualOpsTab = ({ tenant }: { tenant: string }) => {
  const { t } = useTranslation("translation", { useSuspense: false }) as { t: Translate }
  const { ticker } = useTokenIdentity(tenant)
  const { darkMode } = useProps()
  const { has } = usePermissions()

  // Spec §8.4 calls these three the insider-abuse surface. Seeing the page is
  // PAGE_TOKEN; running any of them is its own permission, re-checked server-side.
  const canRun = has(PERMISSIONS.TOKEN_MANUAL_OPS)

  const [open, setOpen] = useState<OpKind | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const [userId, setUserId] = useState("")
  const [amount, setAmount] = useState("")
  const [purchaseId, setPurchaseId] = useState("")
  const [positionId, setPositionId] = useState("")
  const [includeRewards, setIncludeRewards] = useState(true)

  const { key: opKey, reset: resetOpKey } = useIdempotencyKey()

  const start = (kind: OpKind) => {
    setError(undefined)
    setUserId("")
    setAmount("")
    setPurchaseId("")
    setPositionId("")
    setIncludeRewards(true)
    // A NEW intent gets a NEW key. Retries of this same intent reuse it.
    resetOpKey()
    setOpen(kind)
  }

  // Signed: a negative amount removes tokens. Zero is refused by the server as
  // an audit row claiming an intervention that did not happen.
  const amountInvalid = !isDecimalString(amount, { allowNegative: true }) || !/[1-9]/.test(amount)

  const submit = async (reason: string) => {
    setSubmitting(true)
    setError(undefined)

    const res =
      open === "adjust"
        ? await AdminTokenService.adjustBalance(tenant, reason, userId.trim(), amount.trim(), opKey)
        : open === "reconcile"
        ? await AdminTokenService.reconcilePurchase(tenant, reason, purchaseId.trim(), opKey)
        : await AdminTokenService.terminatePosition(tenant, reason, positionId.trim(), includeRewards, opKey)

    setSubmitting(false)

    if (!res.ok) {
      // Kept in the dialog rather than fired as a toast: PURCHASE_FINAL in
      // particular is a business rule the operator has to read and understand,
      // not a transient error to dismiss.
      setError(tokenErrorCopy(t, res.code, res.message))
      return
    }

    if (open === "adjust") {
      const d = res.data as { new_token_balance: string } | undefined
      toast.success(t("token.adjustedToast", { balance: formatDecimalString(d?.new_token_balance, 6) }))
    } else if (open === "reconcile") {
      const d = res.data as { usd_returned_cents: string; token_removed: string } | undefined
      toast.success(
        t("token.reconciledToast", {
          usd: formatUsdCents(d?.usd_returned_cents),
          tokens: formatDecimalString(d?.token_removed, 6),
          ticker
        })
      )
    } else {
      const d = res.data as { principal_returned: string; rewards_paid: string } | undefined
      toast.success(
        t("token.terminatedToast", {
          principal: formatDecimalString(d?.principal_returned, 6),
          rewards: formatDecimalString(d?.rewards_paid, 6)
        })
      )
    }

    setOpen(null)
  }

  return (
    <div className="grid gap-8">
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-5">
        <p className="font-medium text-red-400">{t("token.manualOpsWarningTitle")}</p>
        <p className={clsx("mt-2 text-sm", darkMode ? "text-gray-400" : "text-gray-600")}>{t("token.manualOpsWarningBody")}</p>
      </div>

      {!canRun && <p className="text-sm text-amber-400">{t("token.manualOpsReadOnly")}</p>}

      <div className="grid gap-5 lg:grid-cols-3">
        {OPS.map((op) => (
          <div key={op.kind} className={clsx("flex flex-col rounded-lg border p-5", darkMode ? "border-[rgba(255,255,255,0.1)] bg-[#0C0C1A]" : "border-gray-200 bg-white")}>
            <h4 className="text-lg font-semibold">{t(op.titleKey)}</h4>
            <p className={clsx("mt-2 flex-1 text-sm", darkMode ? "text-gray-400" : "text-gray-600")}>{t(op.blurbKey, { ticker })}</p>
            <p className="mt-3 text-xs text-amber-400">{t(op.warningKey)}</p>
            <button
              type="button"
              onClick={() => start(op.kind)}
              disabled={!canRun}
              className="mt-5 rounded-lg bg-red-600 px-4 py-2 text-sm text-white transition-all hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t(op.titleKey)}
            </button>
          </div>
        ))}
      </div>

      <ReasonModal
        open={open === "adjust"}
        danger
        title={t("token.adjustTitle", { ticker })}
        confirmLabel={t("token.adjustBalance")}
        submitting={submitting}
        error={error}
        canSubmit={!!userId.trim() && !amountInvalid}
        onCancel={() => setOpen(null)}
        onConfirm={submit}
        description={t("token.adjustDesc")}
      >
        <TokenField label={t("token.userIdLabel")} value={userId} onChange={setUserId} placeholder="uuid" />
        <TokenField
          label={t("token.amountLabel")}
          value={amount}
          onChange={setAmount}
          invalid={amountInvalid}
          suffix={ticker}
          hint={
            amountInvalid
              ? t("token.amountSignedInvalid")
              : amount.trim().startsWith("-")
              ? t("token.amountRemovingHint")
              : t("token.amountAddingHint")
          }
        />
      </ReasonModal>

      <ReasonModal
        open={open === "reconcile"}
        danger
        title={t("token.reconcilePurchase")}
        confirmLabel={t("token.reconcileConfirm")}
        submitting={submitting}
        error={error}
        canSubmit={!!purchaseId.trim()}
        onCancel={() => setOpen(null)}
        onConfirm={submit}
        description={
          <>
            <p>{t("token.reconcileDescMain")}</p>
            <p className="mt-2 text-amber-400">{t("token.reconcileDescWarning")}</p>
          </>
        }
      >
        <TokenField label={t("token.purchaseIdLabel")} value={purchaseId} onChange={setPurchaseId} placeholder="uuid" />
      </ReasonModal>

      <ReasonModal
        open={open === "terminate"}
        danger
        title={t("token.terminateTitle")}
        confirmLabel={t("token.terminateConfirm")}
        submitting={submitting}
        error={error}
        canSubmit={!!positionId.trim()}
        onCancel={() => setOpen(null)}
        onConfirm={submit}
        description={t("token.terminateDesc")}
      >
        <TokenField label={t("token.positionIdLabel")} value={positionId} onChange={setPositionId} placeholder="uuid" />
        <label className="flex cursor-pointer items-center gap-3">
          <input type="checkbox" checked={includeRewards} onChange={() => setIncludeRewards(!includeRewards)} className="h-5 w-5 accent-[rgba(132,0,168,1)]" />
          <span>
            <span className="block text-sm font-medium">{t("token.payAccruedRewards")}</span>
            <span className={clsx("block text-xs", darkMode ? "text-gray-500" : "text-gray-500")}>{t("token.payAccruedRewardsHint")}</span>
          </span>
        </label>
      </ReasonModal>
    </div>
  )
}
